import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import billingService from "../../services/billingService";
import SEO from "../../components/SEO/SEO";
import LandingHero from "../../components/landing/LandingHero";
import BookingSteps from "../../components/landing/BookingSteps";
import ListingGrid, { ListingSubcategory } from "../../components/landing/ListingGrid";
import { FinalCtaCard, PartnerSection } from "../../components/landing/PartnerAndCta";
import PlaySearchBar from "./components/PlaySearchBar";
import PlayIntro, { PLAY_COLORS } from "./components/PlayIntro";
import PlayTypes from "./components/PlayTypes";
import PlayEvents from "./components/PlayEvents";
import PlayConcierge from "./components/PlayConcierge";
import PlayYourDay from "./components/PlayYourDay";
import PlayMakeMore from "./components/PlayMakeMore";
import logger from "../../utils/logger";
import "./index.css";

const STEPS = [
  { title: "Discover", description: "Find experiences near you or in the place you're travelling to." },
  { title: "Explore", description: "Compare available experiences and venues." },
  { title: "Book", description: "Check availability and reserve through SPACIA." },
  { title: "Experience", description: "Enjoy your wellness session, meal, adventure or event." },
  { title: "Review", description: "Share your experience and help other users discover great places." },
];

// Shown when a live listing has no photo of its own
const FALLBACK_IMAGES = [
  "/figma-assets/play/intro-wellness.jpg",
  "/figma-assets/play/intro-dining.jpg",
  "/figma-assets/play/intro-adventure.jpg",
  "/figma-assets/play/intro-events.jpg",
  "/figma-assets/play/wellness.jpg",
  "/figma-assets/play/dining.jpg",
];

export default function PlayLandingScreen() {
  const history = useHistory();
  const [subcategories, setSubcategories] = useState<ListingSubcategory[]>([]);

  // Play category is ID 3 in the public registry
  useEffect(() => {
    billingService
      .getPublicServiceSubcategories(3)
      .then((res: any) => {
        const subs: any[] = Array.isArray(res.data)
          ? res.data
          : res.data?.data || res.data?.content || res.data?.items || [];
        if (subs.length > 0) setSubcategories(subs);
      })
      .catch((err: any) => logger.warn("PlayLanding: failed loading subcategories for category 3:", err));
  }, []);

  return (
    <div className="play-landing-container min-h-screen flex flex-col">
      <SEO
        title="Make the Most of Your Time - SPACIA PLAY"
        description="Make the most of your time, wherever you are — from unwinding after a long day to exploring somewhere new."
      />

      <LandingHero
        image="/figma-assets/play/hero.jpg"
        imageAlt="Friends enjoying an experience"
        badge="SPACIA PLAY"
        titleLines={["Make the Most", "of Your Time"]}
        titleSize="xl"
        lead="Make the most of your time, wherever you are — from unwinding after a long day to exploring somewhere new."
        pills={["Wellness", "Dining", "Adventure", "Events", "Concierge"]}
        primaryCta={{ label: "Explore Play", onClick: () => history.push("/workspaces-search?type=play") }}
        secondaryCta={{ label: "Get Started", onClick: () => history.push("/package-signup") }}
        desktopHeight={923}
        shade={{ bottom: 0, side: 0, lead: 1 }}
        search={<PlaySearchBar subcategories={subcategories} />}
      />
      <PlayIntro subcategories={subcategories} />
      <PlayTypes subcategories={subcategories} />
      <PlayEvents subcategories={subcategories} />
      <PlayConcierge />
      <ListingGrid
        categoryId={3}
        eyebrow="Browse"
        title="Explore Experiences"
        subcategories={subcategories}
        fallbackImages={FALLBACK_IMAGES}
        itemLabel="Experience"
        emptyText="No experiences are listed here yet."
        emptyCta={{ label: "Search all experiences", to: "/workspaces-search?type=play" }}
        showDescription
        // API names can be singular ("Event"), so match on the prefix
        badgeColor={name => {
          const key = Object.keys(PLAY_COLORS).find(k => k.startsWith(name.toLowerCase()));
          return key ? PLAY_COLORS[key] : "#017bef";
        }}
      />
      <PlayYourDay />
      <BookingSteps eyebrow="Simple Process" title="How Play Works" steps={STEPS} />
      <PlayMakeMore />
      <PartnerSection
        eyebrow="For Partners"
        title="Have an Experience to Share?"
        description="List your coworking space, hotel, apartment, wellness studio, restaurant or event venue and reach a growing community of users."
        tags={["Wellness providers", "Restaurants", "Tour operators", "Event venues", "Experience providers"]}
        image="/figma-assets/play/partner.jpg"
        imageAlt="Guests enjoying a safari sundowner"
        imageSide="right"
      />
      <FinalCtaCard
        eyebrow="SPACIA PLAY"
        title="Make the Most of Your Time"
        description="From unwinding after a long day to exploring somewhere new, SPACIA helps you discover and book the experiences that make a trip or a day worth remembering."
        primaryCta={{ label: "Explore Play", onClick: () => history.push("/workspaces-search?type=play") }}
        secondaryCta={{ label: "Get Started", onClick: () => history.push("/package-signup") }}
      />
    </div>
  );
}
